const LOGOUT_REDIRECT_KEY = 'logoutRedirectUri'
const DEFAULT_LOGOUT_REDIRECT = '/'

// Keeps redirect hints scoped to a single tab even if storage APIs fail.
let inMemoryLogoutRedirectUri: string | null = null
let legacyStorageCleared = false

const clearLegacyStorage = () => {
  if (legacyStorageCleared || typeof window === 'undefined') {
    return
  }

  legacyStorageCleared = true

  try {
    // Drop legacy localStorage state so redirects don't survive new sessions.
    window.localStorage.removeItem(LOGOUT_REDIRECT_KEY)
  } catch {
    // Local storage might be blocked; ignore cleanup failures.
  }
}

const getStorage = () => {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const storage = window.sessionStorage
    // Touch the key to ensure access isn't blocked.
    storage.getItem(LOGOUT_REDIRECT_KEY)
    clearLegacyStorage()
    return storage
  } catch {
    clearLegacyStorage()
    return null
  }
}

export const setLogoutRedirectUri = (uri?: string | null) => {
  const storage = getStorage()
  if (!storage) {
    inMemoryLogoutRedirectUri = uri || null
    return
  }

  if (uri) {
    try {
      storage.setItem(LOGOUT_REDIRECT_KEY, uri)
    } catch {
      // If sessionStorage becomes unavailable mid-session, fall back to memory.
    }
    inMemoryLogoutRedirectUri = uri
    return
  }

  try {
    storage.removeItem(LOGOUT_REDIRECT_KEY)
  } catch {
    // Ignore storage write errors and fall back to memory state below.
  }
  inMemoryLogoutRedirectUri = null
}

export const getLogoutRedirectUri = () => {
  const storage = getStorage()
  if (!storage) {
    return inMemoryLogoutRedirectUri || DEFAULT_LOGOUT_REDIRECT
  }

  try {
    return storage.getItem(LOGOUT_REDIRECT_KEY) || DEFAULT_LOGOUT_REDIRECT
  } catch {
    return inMemoryLogoutRedirectUri || DEFAULT_LOGOUT_REDIRECT
  }
}

// The MSAL "postLogoutRedirectUri" must be a registered app URL, not an arbitrary per-user destination.
// We always bounce through `/logout`, which then performs the final redirect using `getLogoutRedirectUri()`.
export const getMsalPostLogoutRedirectUri = () => {
  if (typeof window === 'undefined') {
    return '/logout'
  }

  try {
    return new URL('/logout', window.location.origin).toString()
  } catch {
    return `${window.location.origin}/logout`
  }
}
